import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Money } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

export default function Cart() {
    const { items, updateQty, removeItem, clear, totalPrice, totalQty } = useCart();
    const { user, requireAuth } = useAuth();
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState(user?.phone || "");
    const [placing, setPlacing] = useState(false);
    const nav = useNavigate();

    async function place() {
        if (!items.length) return;
        if (!address.trim() || !phone.trim()) {
            toast.error("Enter a delivery address and phone number.");
            return;
        }
        setPlacing(true);
        try {
            const { data } = await api.post("/orders", {
                items: items.map(({ product_id, name, price, quantity }) => ({
                    product_id,
                    name,
                    price,
                    quantity,
                })),
                address: address.trim(),
                phone: phone.trim(),
            });
            clear();
            toast.success("Order placed. Cash on delivery.");
            nav(`/orders?highlight=${data.order_id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Please try again.");
        } finally {
            setPlacing(false);
        }
    }

    return (
        <div className="pb-28 md:pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-8 md:py-12">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={14} /> Continue browsing
                    </Link>
                    <h1 className="font-display mt-3 text-4xl text-veda-onSurface md:text-5xl">
                        Your cart
                    </h1>
                    <p className="mt-1 text-sm text-veda-muted">
                        {totalQty} item{totalQty !== 1 ? "s" : ""}
                    </p>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="veda-container py-16 text-center">
                    <p className="text-sm text-veda-muted">
                        Your cart is empty.
                    </p>
                    <Link
                        to="/"
                        className="mt-6 inline-block bg-veda-inverse px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse"
                    >
                        Browse remedies
                    </Link>
                </div>
            ) : (
                <div className="veda-container grid gap-10 py-10 md:grid-cols-[1.4fr_1fr] md:gap-14">
                    <ul className="divide-y divide-veda-divider border-y border-veda-divider md:border-t-0">
                        {items.map((i) => (
                            <li
                                key={i.product_id}
                                className="grid grid-cols-[80px_1fr_auto] gap-4 py-5 md:grid-cols-[96px_1fr_auto]"
                            >
                                <img
                                    src={i.image_url}
                                    alt={i.name}
                                    className="h-20 w-20 object-cover md:h-24 md:w-24"
                                />
                                <div className="min-w-0">
                                    <p className="line-clamp-2 text-sm font-semibold text-veda-onSurface md:text-base">
                                        {i.name}
                                    </p>
                                    <p className="mt-1 text-sm text-veda-onSurface">
                                        <Money value={i.price} />
                                    </p>
                                    <div className="mt-3 flex items-center gap-2">
                                        <button
                                            data-testid={TID.cartQtyDec(i.product_id)}
                                            onClick={() =>
                                                updateQty(i.product_id, i.quantity - 1)
                                            }
                                            className="grid h-7 w-7 place-items-center border border-veda-border"
                                            aria-label="Decrease"
                                        >
                                            <Minus size={12} />
                                        </button>
                                        <span className="min-w-[24px] text-center text-sm">
                                            {i.quantity}
                                        </span>
                                        <button
                                            data-testid={TID.cartQtyInc(i.product_id)}
                                            onClick={() =>
                                                updateQty(i.product_id, i.quantity + 1)
                                            }
                                            className="grid h-7 w-7 place-items-center border border-veda-border"
                                            aria-label="Increase"
                                        >
                                            <Plus size={12} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end justify-between">
                                    <button
                                        onClick={() => removeItem(i.product_id)}
                                        className="text-veda-error"
                                        aria-label="Remove"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <span className="text-sm font-semibold text-veda-onSurface">
                                        <Money value={i.price * i.quantity} />
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <aside className="md:sticky md:top-24 md:self-start">
                        <div className="border border-veda-border bg-veda-surface p-6">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Delivery details
                            </p>
                            <textarea
                                data-testid={TID.cartAddressInput}
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                rows={3}
                                placeholder="Delivery address"
                                className="mt-3 w-full border border-veda-border bg-veda-surface p-3 text-sm focus:border-veda-brand focus:outline-none"
                            />
                            <input
                                data-testid={TID.cartPhoneInput}
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Phone number"
                                className="mt-2 w-full border border-veda-border bg-veda-surface p-3 text-sm focus:border-veda-brand focus:outline-none"
                            />
                            <div className="mt-5 flex items-baseline justify-between border-t border-veda-divider pt-4">
                                <p className="text-sm text-veda-muted">
                                    Total (COD)
                                </p>
                                <p className="font-display text-3xl text-veda-onSurface">
                                    <Money value={totalPrice} />
                                </p>
                            </div>
                            <button
                                data-testid={`${TID.placeOrderBtn}-desktop`}
                                onClick={() => requireAuth(place)}
                                disabled={placing}
                                className="mt-5 hidden w-full items-center justify-center gap-2 bg-veda-inverse px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-veda-onInverse disabled:opacity-70 md:inline-flex"
                            >
                                {placing && <Loader2 size={14} className="animate-spin" />}
                                Place order · COD
                            </button>
                        </div>
                    </aside>
                </div>
            )}

            {items.length > 0 && (
                <div className="fixed inset-x-0 bottom-0 z-20 border-t border-veda-border bg-veda-surface md:hidden">
                    <div className="veda-container py-3">
                        <button
                            data-testid={TID.placeOrderBtn}
                            onClick={() => requireAuth(place)}
                            disabled={placing}
                            className="inline-flex w-full items-center justify-center gap-2 bg-veda-inverse px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-veda-onInverse disabled:opacity-70"
                        >
                            {placing && <Loader2 size={14} className="animate-spin" />}
                            Place order · COD
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
